import {
    getInitialViewSettings,
    UserViewSettings,
    UserViewSettingsProp,
    ViewIdentifiable
} from "../../models/common/ViewSettings";
import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {
    Project,
    ProjectId,
    ProjectListItem,
    ProjectModule,
} from "../../models/projects/Project";
import {ApiReturnType, getUserViewSettingsApiRequest} from "../../models/common/ApiReturnType";
import {
    sendUserToPage,
    ViewActionTypes
} from "../common/UserViewSettingsActions";
import {AppState} from "../../reducers/RootReducer";
import { ofType, StateObservable} from "redux-observable";
import {filter, switchMap, map, catchError, mergeMap } from "rxjs/operators";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {eventNotificationError, eventNotificationSuccess} from "../common/EventNotificationActions";
import { ajax } from "rxjs/ajax";
import { Observable, of } from 'rxjs';
import {Nullable} from "../../models/common/Nullable";
import {getUrlParamsFromViewSettings, RouteConfig, RouteMethods} from "../../config/RouteConfig";
import {Identifier} from "../../models/common/Identifier";
import {ProjectAppendix} from "../../models/projects/ProjectAppendix";
import {ProjectAppendixModule} from "../../models/projects/ProjectAppendixModule";
import {ProjectStageId} from "../../models/projects/ProjectStage";
import {TotalsListReducerStateType} from "../../reducers/BasicReducerState";
import {ProjectReportBudget} from "../../models/reporting/ProjectReportBudget";



/** backend api path */
function apiPath( 
    projectId: Identifier<number>, 
    id: Nullable< number > = null 
): string {
    if( id === null ) {
        return APP_CONFIG.baseWeb + `projects/${ projectId.rawValue }/appendix`;
    } else {
        return `${ APP_CONFIG.baseWeb }projects/${ projectId.rawValue }/appendix/${ id }`;
    }
}

/** union of all Project related action types */
export type ProjectAppendixViewActionTypes = FetchProjectAppendixActionTypes
    | FetchProjectAppendixByIdActionTypes
    | ViewActionTypes
    | CreateProjectAppendixActionTypes
    | UpdateProjectAppendixActionTypes
    | DeleteProjectAppendixActionTypes
    | FetchNextProjectAppendixNumberActionTypes


/**
 * fetch list of projects action types
 */
export interface FetchProjectAppendixAction extends UserViewSettingsProp, ReduxAction< typeof FETCH_PROJECT_APPENDIX > {
    projectId: Identifier<number>
}
export interface FetchProjectAppendixSuccessAction extends ReduxAction<typeof FETCH_PROJECT_APPENDIX_SUCCESS > {
    payload: TotalsListReducerStateType< ProjectAppendix[], ProjectReportBudget >
}
export interface FetchProjectAppendixErrorAction extends ErrorAction< typeof FETCH_PROJECT_APPENDIX_ERROR >{}

export type FetchProjectAppendixActionTypes = FetchProjectAppendixAction
    | FetchProjectAppendixErrorAction
    | FetchProjectAppendixSuccessAction

/**
 * redux action constants
 */
export const FETCH_PROJECT_APPENDIX = "FETCH_PROJECT_APPENDIX";
export const FETCH_PROJECT_APPENDIX_SUCCESS = "FETCH_PROJECT_APPENDIX_SUCCESS";
export const FETCH_PROJECT_APPENDIX_ERROR = "FETCH_PROJECT_APPENDIX_ERROR";

/**
 * redux action functions
 */
export function fetchProjectAppendix(
    projectId: Identifier< number >,
    userViewSettings: UserViewSettings
) : FetchProjectAppendixAction {
    return { type: FETCH_PROJECT_APPENDIX, userViewSettings, projectId };
}
export function fetchProjectAppendixSuccess(
    payload: TotalsListReducerStateType< ProjectAppendix[], ProjectReportBudget >
) : FetchProjectAppendixSuccessAction {
    return { type: FETCH_PROJECT_APPENDIX_SUCCESS, payload };
}
export function fetchProjectAppendixError(  error: Error ): FetchProjectAppendixErrorAction {
    return { type: FETCH_PROJECT_APPENDIX_ERROR,  error };
}

/**
 * redux observable to fetch projects from api
 */
export function fetchProjectAppendixEpic(
    action$: Observable< FetchProjectAppendixAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType(FETCH_PROJECT_APPENDIX),
        switchMap(action => {
            const path = `${ apiPath( action.projectId ) }${  getUrlParamsFromViewSettings( action.userViewSettings) }`;

            return ajax(
                APP_CONFIG.getRequestObject( path )
            ).pipe(
                map(response => fetchProjectAppendixSuccess(
                    response.response as TotalsListReducerStateType< ProjectAppendix[], ProjectReportBudget >
                )),
                catchError(error => of(
                    fetchProjectAppendixError( error ),
                    eventNotificationError(error, store$, ProjectModule.identifier, "fetchProjectAppendix")
                ))
            );

        })
    );
}


/**
 * fetch a single project for detail
 */
export interface FetchProjectAppendixByIdAction extends ProjectId, ProjectStageId, ReduxAction<typeof FETCH_PROJECT_APPENDIX_BY_ID> {

}
export interface FetchProjectAppendixByIdSuccessAction extends ProjectId, ReduxAction<typeof FETCH_PROJECT_APPENDIX_BY_ID_SUCCESS> {
    payload: ProjectAppendix
}
export interface FetchProjectAppendixByIdErrorAction extends ProjectId, ErrorAction<typeof FETCH_PROJECT_APPENDIX_BY_ID_ERROR> {}

export type FetchProjectAppendixByIdActionTypes = FetchProjectAppendixByIdAction
    | FetchProjectAppendixByIdSuccessAction
    | FetchProjectAppendixByIdErrorAction

// redux action constants
export const FETCH_PROJECT_APPENDIX_BY_ID = "FETCH_PROJECT_APPENDIX_BY_ID";
export const FETCH_PROJECT_APPENDIX_BY_ID_SUCCESS = "FETCH_PROJECT_APPENDIX_BY_ID_SUCCESS";
export const FETCH_PROJECT_APPENDIX_BY_ID_ERROR = "FETCH_PROJECT_APPENDIX_BY_ID_ERROR";

//redux action functions
export function fetchProjectAppendixById(
    projectId: Identifier<number> ,
    projectStageId: Identifier<number>
): FetchProjectAppendixByIdAction {
    return { type: FETCH_PROJECT_APPENDIX_BY_ID, projectId, projectStageId };
}
export function fetchProjectAppendixByIdSuccess( projectId: Identifier<number>, payload: ProjectAppendix ): FetchProjectAppendixByIdSuccessAction {
    return { type: FETCH_PROJECT_APPENDIX_BY_ID_SUCCESS, projectId, payload };
}
export function fetchProjectAppendixByIdError( projectId: Identifier<number>, error: any ): FetchProjectAppendixByIdErrorAction {
    return { type: FETCH_PROJECT_APPENDIX_BY_ID_ERROR, projectId, error };
}

//redux observable epic
export function fetchProjectAppendixByIdEpic(
    action$: Observable< FetchProjectAppendixByIdAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_PROJECT_APPENDIX_BY_ID ),
        switchMap( action => {
            const path = apiPath( action.projectId, action.projectStageId.rawValue );

            return ajax( APP_CONFIG.getRequestObject( path ) ).pipe(
                map( response => fetchProjectAppendixByIdSuccess(
                    action.projectId,
                    response.response as ProjectAppendix
                ) ),
                catchError( error => of(
                    fetchProjectAppendixByIdError( action.projectId, error ),
                    eventNotificationError( error, store$, ProjectModule.identifier, "fetchProjectAppendixById" )
                ) )
            )
        } )
    );
}

/**
 * create a new project action
 */
export interface CreateProjectAppendixAction extends ReduxAction<typeof CREATE_PROJECT_APPENDIX> {
    projectId: Identifier< number >
    appendix: ProjectAppendix
}
export interface CreateProjectAppendixSuccessAction extends ReduxAction<typeof CREATE_PROJECT_APPENDIX_SUCCESS> {
    projectId: Identifier<number>
    appendix: ProjectAppendix
}
export interface CreateProjectAppendixErrorAction extends ErrorAction<typeof CREATE_PROJECT_APPENDIX_ERROR> {
}

export type CreateProjectAppendixActionTypes = CreateProjectAppendixAction
    | CreateProjectAppendixSuccessAction
    | CreateProjectAppendixErrorAction

/**
 * create project redux action constants
 */
export const CREATE_PROJECT_APPENDIX = "CREATE_PROJECT_APPENDIX";
export const CREATE_PROJECT_APPENDIX_SUCCESS = "CREATE_PROJECT_APPENDIX_SUCCESS";
export const CREATE_PROJECT_APPENDIX_ERROR = "CREATE_PROJECT_APPENDIX_ERROR";

/**
 * CREATE PROJECT redux action function
 */
export function createProjectAppendix(
    projectId: Identifier< number >,
    appendix: ProjectAppendix
): CreateProjectAppendixAction {
    return {  type: CREATE_PROJECT_APPENDIX, projectId, appendix };
}
export function createProjectAppendixSuccess(
    projectId: Identifier<number>,
    appendix: ProjectAppendix
): CreateProjectAppendixSuccessAction {
    return { type: CREATE_PROJECT_APPENDIX_SUCCESS, projectId, appendix };
}
export function createProjectAppendixError( error: any ): CreateProjectAppendixErrorAction {
    return { type: CREATE_PROJECT_APPENDIX_ERROR, error };
}

/**
 * create project redux observable epic
 */
export function createProjectAppendixEpic(
    action$: Observable< CreateProjectAppendixAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( CREATE_PROJECT_APPENDIX ),
        switchMap( action => {
            const sendUserTo = RouteConfig.project.routes.detail.link!( action.projectId );

            return ajax( APP_CONFIG.getRequestObject(
                apiPath( action.projectId ),
                RouteMethods.post,
                action.appendix
            ) ).pipe(
                mergeMap( _ => of(
                    createProjectAppendixSuccess( action.projectId, action.appendix as ProjectAppendix ),
                    eventNotificationSuccess(),
                    sendUserToPage( sendUserTo, ProjectModule.identifier )
                ) ),
                catchError( error => {

                    return of(
                        createProjectAppendixError( error ),
                        eventNotificationError( error, store$, 'projects', 'createProjectAppendix' )
                    )
                } )
            );
        } )
    )
}

/**
 * update a new project appendix action
 */
export interface UpdateProjectAppendixAction extends ReduxAction<typeof UPDATE_PROJECT_APPENDIX> {
    appendix: ProjectAppendix
}
export interface UpdateProjectAppendixSuccessAction extends ReduxAction<typeof UPDATE_PROJECT_APPENDIX_SUCCESS> {
    appendix: ProjectAppendix
}
export interface UpdateProjectAppendixErrorAction extends ErrorAction<typeof UPDATE_PROJECT_APPENDIX_ERROR> {
    appendix: ProjectAppendix
}

export type UpdateProjectAppendixActionTypes = UpdateProjectAppendixAction | UpdateProjectAppendixSuccessAction | UpdateProjectAppendixErrorAction

/**
 * create appendix redux action constants
 */
export const UPDATE_PROJECT_APPENDIX = "UPDATE_PROJECT_APPENDIX";
export const UPDATE_PROJECT_APPENDIX_SUCCESS = "UPDATE_PROJECT_APPENDIX_SUCCESS";
export const UPDATE_PROJECT_APPENDIX_ERROR = "UPDATE_PROJECT_APPENDIX_ERROR";

/**
 * CREATE PROJECT_APPENDIX redux action function
 */
export function updateProjectAppendix( appendix: ProjectAppendix ): UpdateProjectAppendixAction {
    return {  type: UPDATE_PROJECT_APPENDIX, appendix };
}
export function updateProjectAppendixSuccess( appendix: ProjectAppendix ): UpdateProjectAppendixSuccessAction {
    return { type: UPDATE_PROJECT_APPENDIX_SUCCESS, appendix };
}
export function updateProjectAppendixError( appendix: ProjectAppendix, error: any ): UpdateProjectAppendixErrorAction {
    return { type: UPDATE_PROJECT_APPENDIX_ERROR, appendix, error };
}

/**
 * create appendix redux observable epic
 */
export function updateProjectAppendixEpic(
    action$: Observable< UpdateProjectAppendixAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( UPDATE_PROJECT_APPENDIX ),
        switchMap( action => {
            const sendUserTo = RouteConfig.project.routes.detail.link!( action.appendix.projectId );

            return ajax( APP_CONFIG.getRequestObject(
                apiPath(
                    action.appendix.projectId,
                    action.appendix.projectStageId.rawValue
                ),
                RouteMethods.put,
                action.appendix
            ) ).pipe(
                mergeMap( _ => of(
                    updateProjectAppendixSuccess( action.appendix as ProjectAppendix ),
                    eventNotificationSuccess(),
                    sendUserToPage( sendUserTo, ProjectAppendixModule.identifier )
                ) ),
                catchError( error => {

                    return of(
                        updateProjectAppendixError( action.appendix, error ),
                        eventNotificationError( error, store$, 'projects', 'updateProjectAppendix' )
                    )
                } )
            );
        } )
    )
}

/**
 * delete a new project appendix action
 */
export interface DeleteProjectAppendixAction extends ReduxAction<typeof DELETE_PROJECT_APPENDIX> {
    projectId: Identifier<number>
    projectStageId: Identifier<number>
}
export interface DeleteProjectAppendixSuccessAction extends ReduxAction<typeof DELETE_PROJECT_APPENDIX_SUCCESS> {
}
export interface DeleteProjectAppendixErrorAction extends ErrorAction<typeof DELETE_PROJECT_APPENDIX_ERROR> {
}

export type DeleteProjectAppendixActionTypes = DeleteProjectAppendixAction | DeleteProjectAppendixSuccessAction | DeleteProjectAppendixErrorAction

/**
 * create appendix redux action constants
 */
export const DELETE_PROJECT_APPENDIX = "DELETE_PROJECT_APPENDIX";
export const DELETE_PROJECT_APPENDIX_SUCCESS = "DELETE_PROJECT_APPENDIX_SUCCESS";
export const DELETE_PROJECT_APPENDIX_ERROR = "DELETE_PROJECT_APPENDIX_ERROR";

/**
 * CREATE PROJECT_APPENDIX redux action function
 */
export function deleteProjectAppendix(
    projectId: Identifier<number>,
    projectStageId: Identifier<number>
): DeleteProjectAppendixAction {
    return {  type: DELETE_PROJECT_APPENDIX, projectId, projectStageId };
}
export function deleteProjectAppendixSuccess( ): DeleteProjectAppendixSuccessAction {
    return { type: DELETE_PROJECT_APPENDIX_SUCCESS };
}
export function deleteProjectAppendixError( error: any ): DeleteProjectAppendixErrorAction {
    return { type: DELETE_PROJECT_APPENDIX_ERROR, error };
}

/**
 * create appendix redux observable epic
 */
export function deleteProjectAppendixEpic(
    action$: Observable< DeleteProjectAppendixAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( DELETE_PROJECT_APPENDIX ),
        switchMap( action => {


            return ajax( APP_CONFIG.getRequestObject(
                apiPath(
                    action.projectId,
                    action.projectStageId.rawValue
                ),
                RouteMethods.delete
            ) ).pipe(
                mergeMap( _ => of(
                    deleteProjectAppendixSuccess(),
                    fetchProjectAppendix( action.projectId, getInitialViewSettings( ProjectAppendixModule.identifier ) ),
                    eventNotificationSuccess(),
                ) ),
                catchError( error => {

                    return of(
                        deleteProjectAppendixError( error ),
                        eventNotificationError( error, store$, 'projects', 'deleteProjectAppendix' )
                    )
                } )
            );
        } )
    )
}

/**
 * fetch next project appendix number
 */
export interface FetchNextProjectAppendixNumberAction extends ReduxAction< typeof FETCH_NEXT_PROJECT_APPENDIX_NUMBER > {
    projectId: Identifier<number>
}
export interface FetchNextProjectAppendixNumberSuccessAction extends ProjectId, ReduxAction<typeof FETCH_NEXT_PROJECT_APPENDIX_NUMBER_SUCCESS > {
    payload: number
}
export interface FetchNextProjectAppendixNumberErrorAction extends ErrorAction< typeof FETCH_NEXT_PROJECT_APPENDIX_NUMBER_ERROR >{}

export type FetchNextProjectAppendixNumberActionTypes = FetchNextProjectAppendixNumberAction
    | FetchNextProjectAppendixNumberErrorAction
    | FetchNextProjectAppendixNumberSuccessAction

/**
 * redux action constants
 */
export const FETCH_NEXT_PROJECT_APPENDIX_NUMBER = "FETCH_NEXT_PROJECT_APPENDIX_NUMBER";
export const FETCH_NEXT_PROJECT_APPENDIX_NUMBER_SUCCESS = "FETCH_NEXT_PROJECT_APPENDIX_NUMBER_SUCCESS";
export const FETCH_NEXT_PROJECT_APPENDIX_NUMBER_ERROR = "FETCH_NEXT_PROJECT_APPENDIX_NUMBER_ERROR";

/**
 * redux action functions
 */
export function fetchNextProjectAppendixNumber(
    projectId: Identifier< number >
) : FetchNextProjectAppendixNumberAction {
    return { type: FETCH_NEXT_PROJECT_APPENDIX_NUMBER, projectId };
}
export function fetchNextProjectAppendixNumberSuccess(
    projectId: Identifier<number>,
    payload: number
) : FetchNextProjectAppendixNumberSuccessAction {
    return { type: FETCH_NEXT_PROJECT_APPENDIX_NUMBER_SUCCESS, payload, projectId };
}
export function fetchNextProjectAppendixNumberError(  error: Error ): FetchNextProjectAppendixNumberErrorAction {
    return { type: FETCH_NEXT_PROJECT_APPENDIX_NUMBER_ERROR,  error };
}

/**
 * redux observable to fetch projects from api
 */
export function fetchNextProjectAppendixNumberEpic(
    action$: Observable< FetchNextProjectAppendixNumberAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_NEXT_PROJECT_APPENDIX_NUMBER ),
        switchMap(action => {
            const path = `${ apiPath( action.projectId ) }/next-number`;

            return ajax(
                APP_CONFIG.getRequestObject( path )
            ).pipe(
                map(response => fetchNextProjectAppendixNumberSuccess(
                    action.projectId,
                    response.response as number
                )),
                catchError(error => of(
                    fetchNextProjectAppendixNumberError( error ),
                    eventNotificationError(error, store$, ProjectModule.identifier, "fetchNextProjectAppendixNumber")
                ))
            );

        })
    );
}
